import { h, Component } from "preact";
import { connect } from "react-redux";
import { css } from "emotion";
import queryString from "query-string";
import {
  getTour,
  getTourPreview,
  getTourFilterOptions
} from "../../state/actions/tourActions";
import { clearEvent } from "../../state/actions/eventActions";
import { clearOrder } from "../../state/actions/orderActions";
import Pagination from "../../components/Pagination";
import TourError from "../../components/TourError";
import Content from "../../components/Content";
import FeaturedEvents from "./FeaturedEvents";
import Events from "./Events";
import Sponsors from "./Sponsors";
import SocialMedia from "./SocialMedia";
import Header from "./Header";
import About from "./About";
import TourGroup from "./TourGroup";
import Filter from "./Filter";
import FilterValueDisplay from "./FilterValueDisplay";
import { updateTitle } from "../../utils/BrowserUtils";

/**
 * Display tour page
 *
 * @class Tour
 * @extends {Component}
 */
class Tour extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedTab: "events",
      currentPage: 1,
      date: "",
      displayDate: "",
      location: ""
    };
  }

  componentDidMount() {
    const {
      slug,
      getTourFilterOptions,
      tourGroupId,
      tours: { tour },
      event,
      order,
      clearEvent,
      clearOrder
    } = this.props;
    const query = { tourGroupId };

    getTourFilterOptions(slug, query);
    if (!tour || (event && event.data)) {
      this.load();
    }
    if (event && event.data) {
      clearEvent();
    }
    if (order && order.data) {
      clearOrder();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.tourGroupId !== prevProps.tourGroupId) {
      const { tourGroupId, slug, getTourFilterOptions } = this.props;
      this.setState({ currentPage: 1 });
      const query = { tourGroupId };
      getTourFilterOptions(slug, query);
      this.load();
    }
    if (this.props.tours.tour !== prevProps.tours.tour) {
      if (this.props.tours.tour && this.props.tours.tour.name) {
        updateTitle(this.props.tours.tour.name);
      }
    }
  }

  filter = (e, dateOrLocation, mobileUpdate = false) => {
    let update = {};

    if (mobileUpdate) {
      update = mobileUpdate;
    } else {
      if (dateOrLocation === "date") {
        const index = e.nativeEvent.target.selectedIndex;
        const text = e.nativeEvent.target[index].text;
        update.displayDate = text;
      }
      update[dateOrLocation] = e.target.value;
    }
    update.currentPage = 1;
    this.setState(update);
    this.load();
  };

  clearFilter = () => {
    this.setState({ currentPage: 1 });
    this.load();
  };

  load = (onlyEventLoading = false) => {
    let token = null;
    if (typeof window !== "undefined") {
      const parsed = queryString.parse(window.location.search);
      token = parsed.token;
    }
    const { slug, preview, getTour, getTourPreview, tourGroupId } = this.props;
    const { currentPage, date, location } = this.state;
    const query = {
      currentPage,
      date,
      location,
      tourGroupId,
      onlyEventLoading
    };
    if (preview) {
      getTourPreview(slug, query);
    } else if (slug) {
      getTour(slug, query, token);
    }
  };

  selectTab = selectedTab => {
    this.setState({ selectedTab });
  };

  selectPagination = currentPage => {
    this.setState({ currentPage });
    const onlyEventLoading = true;
    this.load(onlyEventLoading);
  };

  render() {
    const {
      slug,
      tourGroupId,
      tours: {
        tour,
        loading,
        error,
        tourDates,
        tourLocations,
        eventLoading,
        tourEvents,
        featuredEvents
      }
    } = this.props;
    const {
      selectedTab,
      currentPage,
      displayDate,
      location,
      date
    } = this.state;

    let token, preview;
    if (typeof window !== "undefined") {
      const parsed = queryString.parse(window.location.search);
      token = parsed.token;
      preview = parsed.preview ? parsed.preview : false;
    }

    if (!tour || loading) {
      return (
        <Content>
          <div class="loading loading-lg" />
        </Content>
      );
    }
    if (error) {
      return (
        <Content>
          <TourError type="notFound" />
        </Content>
      );
    }
    if (tour && !tour.published) {
      return (
        <Content>
          <TourError type="notPublished" />
        </Content>
      );
    }

    const pageLayout = tour.settings.pageLayout;
    const listAndDate =
      pageLayout === "list" && tour.settings.listViewType === "date";

    let events = [];
    if (tourEvents && tourEvents.length && featuredEvents) {
      events = tourEvents.filter(event => {
        const isFeaturedEvent = featuredEvents.find(
          featuredEvent => String(event._id) === String(featuredEvent._id)
        );
        if (!isFeaturedEvent) {
          return event;
        }
      });
    }
    const showGroup =
      tour.groups && tour.groups.length && !tourGroupId ? true : false;
    const showSocialMedia = !showGroup && pageLayout === "list" ? true : false;
    const showSocialModal =
      selectedTab === "about" || pageLayout === "tiles" || showGroup
        ? true
        : false;
    const background =
      tour.theme.background.type === "color"
        ? ""
        : tour.theme.background.image.url;
    const styles = {
      wrapper: css`
        background: url(${background})
          ${tour.theme.background.type === "image"
            ? "center center / cover no-repeat"
            : ""}
          ${tour.theme.background.color};
        padding-top: 40px;
        @media (max-width: 600px) {
          padding-top: 0px;
        }
      `,
      container: css`
        max-width: 1002px;
        width: 100%;
        display: ${pageLayout === "tiles" ? "inherit" : "flex"};
        justify-content: space-between;
        margin-left: auto;
        margin-right: auto;
        flex-wrap: wrap;
        @media (max-width: 1000px) {
          ${listAndDate ? "justify-content: center" : ""}
        }
        @media (max-width: 600px) {
          display: flex;
        }
      `,
      searchContainer: css`
        max-width: 1000px;
        margin-top: 0px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        margin-bottom: 12px;
        @media (max-width: 600px) {
          padding-left: 14px;
          padding-right: 14px;
        }
      `,
      paginationWrapper: css`
        max-width: 1000px;
        width: 100%;
        margin-left: auto;
        margin-right: auto;
      `,
      paginationContainer: css`
        max-width: ${pageLayout === "tiles" || showGroup ? "1000px" : "720px"};
      `,
      eventCol: css`
        max-width: ${pageLayout === "tiles" || showGroup ? "1002px" : "720px"};
        width: 100%;
        box-shadow: ${listAndDate
          ? "0 1px 10px 2px rgba(32, 7, 64, 0.13)"
          : "none"};
        border-radius: ${listAndDate ? "4px" : "0px"};
        margin-top: 0px;
        overflow: ${listAndDate ? "hidden" : "visible"};
        @media (max-width: 1000px) {
          ${listAndDate ? "max-width: 100%" : ""};
          margin-left: ${listAndDate ? "14px" : "auto"};
          margin-right: ${listAndDate ? "14px" : "auto"};
        }
        @media (max-width: 600px) {
          max-width: 100%;
          padding-left: ${listAndDate ? "0px" : "14px"};
          padding-right: ${listAndDate ? "0px" : "14px"};
          margin-top: ${listAndDate ? "12px" : "0px"};
        }
      `,
      locationRow: css`
        background: #fff;
        font-size: 20px;
        padding: 16px;
        @media (max-width: 600px) {
          font-size: 14px;
          padding: 16px 12px;
        }
      `
    };

    return (
      <div className={styles.wrapper}>
        <Header
          tour={tour}
          selectTab={this.selectTab}
          selectedTab={selectedTab}
          showSocialModal={showSocialModal}
        />
        {selectedTab === "events" ? (
          <div className="eventContainer">
            {!showGroup ? (
              <div className={styles.searchContainer}>
                <Filter
                  pageLayout={pageLayout}
                  tourDates={tourDates}
                  tourLocations={tourLocations}
                  filter={this.filter}
                  date={date}
                  displayDate={displayDate}
                  location={location}
                  theme={tour.theme}
                  totalSize={tour.totalEventSize}
                  clearFilter={this.clearFilter}
                />
              </div>
            ) : null}

            <div className={styles.container}>
              {showGroup ? (
                <TourGroup
                  slug={slug}
                  groups={tour.groups}
                  theme={tour.theme}
                  preview={preview}
                  token={token}
                />
              ) : (
                <div className={styles.eventCol}>
                  {listAndDate && (
                    <FilterValueDisplay
                      date={displayDate}
                      location={location}
                      listAndDate={true}
                      totalSize={tour.totalEventSize}
                      theme={tour.theme}
                    />
                  )}
                  {eventLoading ? (
                    <div class="loading loading-lg" />
                  ) : (
                    <div>
                      <FeaturedEvents
                        events={featuredEvents}
                        pageLayout={pageLayout}
                        theme={tour.theme}
                        eventListingLayout={tour.settings.eventListingLayout}
                        listViewType={tour.settings.listViewType}
                        showPrice={tour.settings.showPrice}
                      />
                      <Events
                        events={events}
                        pageLayout={pageLayout}
                        theme={tour.theme}
                        eventListingLayout={tour.settings.eventListingLayout}
                        listViewType={tour.settings.listViewType}
                        showPrice={tour.settings.showPrice}
                      />
                    </div>
                  )}
                </div>
              )}
              {showSocialMedia && (
                <SocialMedia
                  slug={slug}
                  listViewType={tour.settings.listViewType}
                  showPrice={tour.settings.showPrice}
                  eventCardBackgroundColor={tour.theme.eventCardBackgroundColor}
                  cardAndBarOpacity={tour.theme.cardAndBarOpacity}
                />
              )}
            </div>
          </div>
        ) : (
          <About
            name={tour.name}
            description={tour.description}
            eventCardBackgroundColor={tour.theme.eventCardBackgroundColor}
            {...tour.social}
          />
        )}
        {!showGroup && selectedTab === "events" ? (
          <div className={styles.paginationWrapper}>
            <div className={styles.paginationContainer}>
              <Pagination
                selectPagination={this.selectPagination}
                currentPage={currentPage}
                totalSize={tour.totalEventSize}
                position="right"
                theme={tour.theme ? tour.theme : null}
              />
            </div>
          </div>
        ) : null}

        <Sponsors
          sponsorsLogos={tour.sponsorsLogos}
          sponsorBoxBackgroundColor={tour.theme.sponsorBoxBackgroundColor}
        />
      </div>
    );
  }
}

export default connect(
  (state, ownProps) => {
    return {
      tours: state.tours,
      event: state.event,
      order: state.order
    };
  },
  {
    getTour,
    getTourPreview,
    getTourFilterOptions,
    clearEvent,
    clearOrder
  }
)(Tour);



// WEBPACK FOOTER //
// ./routes/tours/Tour.js